import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { highlightMatches } from '../../utils';
import type { Customer, PipelineStage, StageSummary } from '../../types';

const SEARCH_RESULT_ESTIMATED_ROW_HEIGHT = 96;

interface GlobalSearchResultsPanelProps {
  totalMatches: number;
  results: Customer[];
  stageSummaries: StageSummary[];
  highlightTerms: string[];
  onSelectCustomer: (customer: Customer) => void;
  onScrollToStage: (stageId: number) => void;
  onClearSearch: () => void;
  focusMatchesOnly: boolean;
  onToggleFocusMatches: () => void;
  stageLookup: Map<number, PipelineStage>;
}

/**
 * Global search results panel with virtualized list
 * Shows matching customers and allows navigation to stages
 */
export function GlobalSearchResultsPanel({
  totalMatches,
  results,
  stageSummaries,
  highlightTerms,
  onSelectCustomer,
  onScrollToStage,
  onClearSearch,
  focusMatchesOnly,
  onToggleFocusMatches,
  stageLookup,
}: GlobalSearchResultsPanelProps) {
  const listRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => SEARCH_RESULT_ESTIMATED_ROW_HEIGHT,
    overscan: 6,
  });

  const hasResults = results.length > 0;

  return (
    <div className="mb-6 rounded-3xl border border-slate-200/70 dark:border-slate-800/60 bg-white/90 dark:bg-slate-900/80 shadow-xl" style={{ contain: 'layout' }}>
      <div className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {hasResults
                ? `${totalMatches} matching deal${totalMatches === 1 ? '' : 's'}`
                : 'No matches yet'}
            </p>
            {highlightTerms.length > 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Searching for{' '}
                {highlightTerms.map((term, index) => (
                  <span key={term}>
                    {index > 0 && ', '}
                    <code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded-md">
                      {term}
                    </code>
                  </span>
                ))}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={focusMatchesOnly ? 'domelec-blue' : 'outline'}
              size="sm"
              onClick={onToggleFocusMatches}
              aria-pressed={focusMatchesOnly}
            >
              {focusMatchesOnly ? 'Focusing matches' : 'Include unmatched'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClearSearch}>
              Clear search
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {stageSummaries
            .filter((summary) => summary.matches > 0)
            .map((summary) => (
              <button
                key={summary.stage.id}
                type="button"
                className="flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:shadow"
                style={{
                  borderColor: summary.stage.color,
                  backgroundColor: `${summary.stage.color}18`,
                }}
                onClick={() => onScrollToStage(summary.stage.id)}
              >
                <span
                  className="inline-flex h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: summary.stage.color }}
                />
                {summary.stage.name}
                <Badge variant="secondary" className="ml-1">
                  {summary.matches}
                </Badge>
              </button>
            ))}
        </div>

        <div
          ref={listRef}
          className="max-h-80 overflow-y-auto pr-1"
          aria-label={`${results.length} search results`}
          style={{ contentVisibility: 'auto', containIntrinsicBlockSize: '500px' }}
        >
          {hasResults ? (
            <div
              className="relative"
              style={{ height: `${virtualizer.getTotalSize()}px` }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const customer = results[virtualRow.index];
                const stage = customer.pipelineStageId
                  ? stageLookup.get(customer.pipelineStageId)
                  : undefined;
                const stageColor = stage?.color ?? '#0f172a';
                const dealValue = customer.dealValue
                  ? `£${(customer.dealValue / 100).toLocaleString('en-GB', {
                      maximumFractionDigits: 0,
                    })}`
                  : 'No value';

                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    className="absolute left-0 w-full px-0"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      paddingBottom: '0.75rem',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectCustomer(customer)}
                      className="w-full text-left rounded-2xl border border-slate-200/70 dark:border-slate-800/60 bg-white/80 dark:bg-slate-900/70 px-4 py-3 shadow-sm hover:border-emerald-300/70 hover:shadow-md transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                            {highlightMatches(
                              customer.name ?? 'Unnamed deal',
                              highlightTerms
                            )}
                          </span>
                          <div className="text-xs text-slate-500 dark:text-slate-400 space-x-2">
                            {customer.email && (
                              <span>
                                {highlightMatches(customer.email, highlightTerms)}
                              </span>
                            )}
                            {customer.phone && (
                              <span>
                                {highlightMatches(customer.phone, highlightTerms)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <span className="font-semibold text-slate-700 dark:text-slate-200">
                            {dealValue}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span
                              className="inline-flex h-2 w-2 rounded-full"
                              style={{ backgroundColor: stageColor }}
                            />
                            {highlightMatches(stage?.name ?? 'No stage', highlightTerms)}
                          </span>
                        </div>
                      </div>
                      {customer.address && (
                        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          {highlightMatches(customer.address, highlightTerms)}
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-6 text-sm text-muted-foreground text-center border border-dashed border-slate-200/70 dark:border-slate-800/60 rounded-2xl">
              Try widening your search or toggling "Include unmatched".
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
